import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { SUPPRESS_ERROR_TOAST, USE_ADMIN_API, USE_OTP_API } from './api.context';
import { environment } from '../../../environments/environments';
import { AuthService } from '../../services/auth/auth.service';
import { ToastService } from '../../services/toast/toast.service';

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const toast = inject(ToastService);

  const useAdminApi = req.context.get(USE_ADMIN_API);
  const useOtpApi = req.context.get(USE_OTP_API);
  const suppressErrorToast = req.context.get(SUPPRESS_ERROR_TOAST);

  const baseUrl = useAdminApi
    ? environment.admin.api_base_url
    : useOtpApi
      ? environment.otp.otp_base_url
      : environment.dms.api_base_url;

  const token = localStorage.getItem('authToken');
  const isFormData = req.body instanceof FormData;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const apiReq = req.clone({
    url: `${baseUrl}${req.url}`,
    setHeaders: headers,
  });

  const isLoginRequest = req.url === '/api/v1/login';

  return next(apiReq).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unexpected error occurred';

      if (error.status === 401 && !isLoginRequest) {
        errorMessage = 'Session expired. Please login again.';
        auth.logout();
        router.navigate(['/login']);
      } else {
        errorMessage = extractErrorMessage(error.error);
      }

      if (!suppressErrorToast && !isLoginRequest) {
        toast.showError(errorMessage);
      }

      return throwError(() => error);
    }),
  );
};

function extractErrorMessage(errorBody: unknown): string {
  if (
    typeof errorBody === 'object' &&
    errorBody !== null &&
    'message' in errorBody &&
    typeof errorBody.message === 'string'
  ) {
    return errorBody.message;
  }

  if (
    typeof errorBody === 'object' &&
    errorBody !== null &&
    'errors' in errorBody &&
    typeof errorBody.errors === 'object' &&
    errorBody.errors !== null
  ) {
    for (const value of Object.values(errorBody.errors)) {
      if (Array.isArray(value) && value.length > 0) {
        return String(value[0]);
      }

      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  return 'An unexpected error occurred';
}